import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Mail, Copy, Check, Share2, MessageCircle, MessageSquare, Linkedin, Facebook } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";

const COPY = {
  news: {
    title: "Weathervane News",
    subtitle: "AI-curated daily news, 6 stories per topic, delivered at your preferred time.",
    shareText:
      "I've been using Weathervane News — one refined topic, six hand-picked stories every morning, no clickbait. Thought you might like it:",
    destination: "/news",
  },
  weather: {
    title: "Weathervane",
    subtitle:
      "A 7-day forecast ensembled from 5 top weather models with a plain-English narrative. No ads, no spam.",
    shareText:
      "I've been using Weathervane — the most accurate daily forecast I've found, pulled from 5 weather models and written up in plain English:",
    destination: "/",
  },
};

function buildShareUrl(kind) {
  const origin =
    process.env.REACT_APP_PUBLIC_URL || window.location.origin || "";
  return `${origin}${COPY[kind]?.destination || "/"}?ref=share`;
}

export default function SharePage() {
  const { kind = "news" } = useParams();
  const data = COPY[kind] || COPY.news;
  const shareUrl = buildShareUrl(kind);
  const [copied, setCopied] = useState(false);

  const openNative = () => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      navigator
        .share({ title: data.title, text: data.shareText, url: shareUrl })
        .catch(() => {});
    } else {
      // Desktop fallback — focus the copy-link field so user can grab the URL
      const el = document.querySelector('[data-testid="share-url-input"]');
      if (el) el.select();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback: select-and-copy prompt
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const encodedText = encodeURIComponent(`${data.shareText} ${shareUrl}`);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTextOnly = encodeURIComponent(data.shareText);

  const channels = [
    {
      key: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(
        `You should try ${data.title}`
      )}&body=${encodedText}`,
    },
    {
      key: "x",
      label: "Post on X",
      iconText: "𝕏",
      href: `https://twitter.com/intent/tweet?text=${encodedTextOnly}&url=${encodedUrl}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedText}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      key: "sms",
      label: "Text message",
      icon: MessageSquare,
      href: `sms:?&body=${encodedText}`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={36} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
        </div>
      </header>
      <section className="max-w-2xl mx-auto px-6 lg:px-12 py-16" data-testid="share-page">
        <div className="ticker-label mb-3">Spread the word</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Share {data.title}.
        </h1>
        <p className="mt-4 text-neutral-700 max-w-xl">{data.subtitle}</p>

        <div className="mt-10 border border-black bg-white p-5">
          <div className="ticker-label mb-3">Share link</div>
          <div className="flex items-stretch gap-2">
            <input
              readOnly
              value={shareUrl}
              data-testid="share-url-input"
              className="flex-1 px-3 py-3 border border-black/30 bg-[#FAFAFA] text-sm outline-none select-all"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={copyLink}
              data-testid="copy-share-link"
              className="btn-brutal px-4 py-3 text-xs uppercase tracking-[0.14em] font-semibold inline-flex items-center gap-2 min-w-[120px] justify-center"
            >
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>

          <button
            onClick={openNative}
            data-testid="native-share-button"
            className="mt-4 w-full px-4 py-3 text-xs uppercase tracking-[0.14em] font-semibold border border-black bg-white hover:bg-[#FFD600] inline-flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" /> Open my share sheet
          </button>
        </div>

        <div className="mt-8">
          <div className="ticker-label mb-3">Or pick a channel</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {channels.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.key}
                  href={c.href}
                  target={c.key === "email" || c.key === "sms" ? undefined : "_blank"}
                  rel="noreferrer"
                  data-testid={`share-channel-${c.key}`}
                  className="border border-black bg-white hover:bg-[#FFD600] px-4 py-4 text-sm font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-3 transition-colors"
                >
                  {Icon ? (
                    <Icon className="w-4 h-4" />
                  ) : (
                    <span className="inline-flex w-4 h-4 items-center justify-center font-display font-black text-[11px]">
                      {c.iconText}
                    </span>
                  )}
                  <span>{c.label}</span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="mt-10 text-xs text-neutral-500">
          Sharing a link bumps us by exactly one reader &mdash; that&rsquo;s how Weathervane grows. Thank you.
        </div>
      </section>
    </div>
  );
}
